import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { lgpdService } from "@/services/lgpdService";
import type {
  IncidentCreate,
  IncidentUpdate,
  RetentionPolicyUpdate,
  SubjectRequestUpdate,
} from "@/types/lgpd";
import type { PiiAccessLogQuery } from "@/types/pii-access-log";

const keys = {
  requests: ["lgpd", "requests"] as const,
  incidents: ["lgpd", "incidents"] as const,
  retention: ["lgpd", "retention"] as const,
  piiAccessLogs: (wsId: number, params?: PiiAccessLogQuery) =>
    ["lgpd", "pii-access-logs", wsId, params ?? "all"] as const,
};

// ─── Subject requests ─────────────────────────────────────────────────

export function useSubjectRequests(enabled = true) {
  return useQuery({
    queryKey: keys.requests,
    queryFn: () => lgpdService.listRequests({ limit: 200 }),
    enabled,
  });
}

export function useUpdateSubjectRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: SubjectRequestUpdate }) =>
      lgpdService.updateRequest(id, payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.requests }),
  });
}

// ─── Incidents ────────────────────────────────────────────────────────

export function useIncidents(enabled = true) {
  return useQuery({
    queryKey: keys.incidents,
    queryFn: () => lgpdService.listIncidents({ limit: 200 }),
    enabled,
  });
}

export function useCreateIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: IncidentCreate) => lgpdService.createIncident(payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.incidents }),
  });
}

export function useUpdateIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: IncidentUpdate }) =>
      lgpdService.updateIncident(id, payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.incidents }),
  });
}

// ─── Retention policies ───────────────────────────────────────────────

export function useRetentionPolicies(enabled = true) {
  return useQuery({
    queryKey: keys.retention,
    queryFn: () => lgpdService.listRetentionPolicies(),
    enabled,
  });
}

export function useUpdateRetentionPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: RetentionPolicyUpdate }) =>
      lgpdService.updateRetentionPolicy(id, payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.retention }),
  });
}

// ─── PII access logs (Art. 37 trail) ──────────────────────────────────

/** FE-028: trilha read-only de acessos a PII, escopada ao workspace ativo. */
export function usePiiAccessLogs(workspaceId: number | null, params?: PiiAccessLogQuery) {
  return useQuery({
    queryKey: keys.piiAccessLogs(workspaceId ?? 0, params),
    queryFn: () => lgpdService.listPiiAccessLogs(workspaceId as number, params),
    enabled: workspaceId != null,
  });
}
